/**
 * Share links (ADR-0013). Three scopes: org (any signed-in member), people
 * (allow-listed users), public (anyone with the link; always expires).
 * Create is idempotent per (target, scope) — the backend returns the existing
 * live link instead of minting a second token, so revoking always kills the
 * one link that's circulating.
 */

import { apiFetch } from "@/api/client";

export type ShareScope = "org" | "people" | "public";

export interface ShareInfo {
  token: string;
  /** Path form ("/share/<token>") — compose with window.location.origin. */
  url: string;
  scope: ShareScope;
  expires_at: string | null;
  allowed_user_ids: string[];
  existing?: boolean;
}

export type ShareTarget =
  | { artifactId: string }
  | { conversationId: string; rel: string };

export interface CreateShareOpts {
  scope?: ShareScope;
  allowedUserIds?: string[];
  expiresInDays?: number;
}

function targetBody(target: ShareTarget): Record<string, string> {
  return "artifactId" in target
    ? { artifact_id: target.artifactId }
    : { conversation_id: target.conversationId, rel: target.rel };
}

export function createShare(target: ShareTarget, opts: CreateShareOpts = {}): Promise<ShareInfo> {
  return apiFetch<ShareInfo>("/shares", {
    method: "POST",
    body: JSON.stringify({
      ...targetBody(target),
      scope: opts.scope ?? "org",
      allowed_user_ids: opts.allowedUserIds ?? null,
      expires_in_days: opts.expiresInDays ?? null,
    }),
  });
}

export function revokeShare(token: string): Promise<{ revoked: boolean }> {
  return apiFetch<{ revoked: boolean }>(`/shares/${encodeURIComponent(token)}`, {
    method: "DELETE",
  });
}

/** One live share as listed by GET /api/shares. ``allowed_user_ids`` is
 *  null unless you created the share (the allow-list is the creator's). */
export interface SharedItem {
  token: string;
  url: string;
  artifact_id: string | null;
  rel: string | null;
  conversation_id: string;
  filename: string;
  file_type: string;
  scope: ShareScope;
  expires_at: string | null;
  shared_by: string;
  mine: boolean;
  allowed_user_ids: string[] | null;
  created_at: string;
  views: number;
}

export function listShares(target?: ShareTarget): Promise<{ shares: SharedItem[] }> {
  const params = target ? `?${new URLSearchParams(targetBody(target))}` : "";
  return apiFetch<{ shares: SharedItem[] }>(`/shares${params}`);
}

export interface DirectoryUser {
  id: string;
  name: string;
  email: string;
}

/** Org members for the people-scope picker (excludes the caller). */
export function listDirectory(): Promise<{ users: DirectoryUser[] }> {
  return apiFetch<{ users: DirectoryUser[] }>("/shares/directory");
}

/** ADMIN-only: every live public link org-wide. 403 for non-admins — the
 *  gallery uses that to decide whether to show the exposure tab at all. */
export function listExposure(): Promise<{ shares: SharedItem[] }> {
  return apiFetch<{ shares: SharedItem[] }>("/shares/exposure");
}
